package com.arena_web.ui.components.forms

import androidx.compose.runtime.*
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.toRgbString
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Input

@Composable
fun TextInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true,
    type: InputType<String> = InputType.Text
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    Column(modifier = modifier.fillMaxWidth().sGap(4)) {
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .sFontSize(14)
                    .color(colors.textSecondary)
                    .sMarginBottom(4)
            )
        }

        Input(type = type) {
            value(value)
            onInput { onValueChange(it.value) }
            if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
            if (!enabled) attr("disabled", "")

            style {
                width(100.percent)
                property("padding", s(12))
                property("border-radius", s(8))
                property("font-size", s(16))
                property("outline", "none")
                property("box-sizing", "border-box")
                property("transition", "border-color 200ms ease")
                property("font-family", "inherit")
                property("background-color", colors.surface.toRgbString())
                property("color", colors.textPrimary.toRgbString())

                if (error != null) {
                    property("border", "1px solid ${AppColors.Error.toRgbString()}")
                } else {
                    property("border", "1px solid ${colors.border.toRgbString()}")
                }

                if (!enabled) {
                    opacity(0.6)
                    property("cursor", "not-allowed")
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .sFontSize(12)
                    .color(AppColors.Error)
                    .sMarginTop(4)
            )
        }
    }
}

@Composable
fun PasswordInput(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true
) {
    TextInput(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier,
        label = label,
        placeholder = placeholder,
        error = error,
        enabled = enabled,
        type = InputType.Password
    )
}

@Composable
fun TextArea(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true,
    rows: Int = 4
) {
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)

    Column(modifier = modifier.fillMaxWidth().sGap(4)) {
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .sFontSize(14)
                    .color(colors.textSecondary)
                    .sMarginBottom(4)
            )
        }

        org.jetbrains.compose.web.dom.TextArea {
            value(value)
            onInput { onValueChange(it.value) }
            attr("rows", rows.toString())
            if (placeholder.isNotEmpty()) attr("placeholder", placeholder)
            if (!enabled) attr("disabled", "")

            style {
                width(100.percent)
                property("padding", s(12))
                property("border-radius", s(8))
                property("font-size", s(16))
                property("outline", "none")
                property("box-sizing", "border-box")
                property("resize", "vertical")
                property("transition", "border-color 200ms ease")
                property("font-family", "inherit")
                property("background-color", colors.surface.toRgbString())
                property("color", colors.textPrimary.toRgbString())

                if (error != null) {
                    property("border", "1px solid ${AppColors.Error.toRgbString()}")
                } else {
                    property("border", "1px solid ${colors.border.toRgbString()}")
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .sFontSize(12)
                    .color(AppColors.Error)
                    .sMarginTop(4)
            )
        }
    }
}
